import React, { useState } from "react";
import { Button, Header, Modal, Confirm, Dropdown } from "semantic-ui-react";
import PropTypes from "prop-types";
import EmailExemptionService from "../../../services/EmailExemptions";
import EmailExemptionForm from "../forms/EmailExemptionForm";

/**
 * Modal component for creating, editing, and cloning email exemptions
 * @param {Object} props - Component props
 * @param {Function} props.fetchExemptions - Function to refresh the exemptions list
 * @param {boolean} props.isClone - Whether this is a clone operation
 * @param {number} props.emailExemptionId - ID of the exemption to edit/clone
 * @param {boolean} props.button - Whether to render as a button instead of dropdown item
 * @param {boolean} props.menuTrigger - Whether to render as a menu item
 * @param {Function} props.onSuccess - Callback function after successful operation
 */
function EmailExemptionsModal({
  fetchExemptions,
  isClone,
  emailExemptionId,
  button,
  menuTrigger,
  onSuccess,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleOpen = () => {
    setModalOpen(true);
  };

  const handleClose = () => {
    setConfirmOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setModalOpen(false);
  };

  const handleSuccess = async () => {
    await fetchExemptions();
    if (onSuccess) {
      onSuccess();
    }
    handleConfirmClose();
  };

  const renderTrigger = () => {
    if (button) {
      return (
        <Button
          content="New Email Exemption"
          onClick={handleOpen}
          size="tiny"
          className="item-adder"
        />
      );
    }

    if (menuTrigger) {
      return (
        <Dropdown.Item content="Delete" icon="trash" onClick={handleOpen} />
      );
    }

    return (
      <Dropdown.Item
        className="primary"
        content={isClone ? "Clone" : "Edit"}
        icon={isClone ? "clone" : "edit"}
        onClick={handleOpen}
      />
    );
  };

  return (
    <>
      {renderTrigger()}
      <Confirm
        className="confirm"
        open={confirmOpen}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        header={isClone ? "Clone Email Exemption" : "Edit Email Exemption"}
        content="Are you sure? Closing this window will undo unsaved changes"
      />
      <Modal
        open={modalOpen}
        onClose={handleClose}
        size="large"
        closeIcon
        closeOnDimmerClick={false}
      >
        <Header
          content={
            isClone
              ? "Clone Email Exemption"
              : emailExemptionId
              ? "Edit Email Exemption"
              : "New Email Exemption"
          }
        />
        <Modal.Content>
          <EmailExemptionForm
            onSuccess={handleSuccess}
            isClone={isClone}
            emailExemptionId={emailExemptionId}
          />
        </Modal.Content>
      </Modal>
    </>
  );
}

EmailExemptionsModal.propTypes = {
  fetchExemptions: PropTypes.func.isRequired,
  isClone: PropTypes.bool,
  emailExemptionId: PropTypes.number,
  button: PropTypes.bool,
  menuTrigger: PropTypes.bool,
  onSuccess: PropTypes.func,
};

EmailExemptionsModal.defaultProps = {
  isClone: false,
  button: false,
  menuTrigger: false,
};

export default EmailExemptionsModal;
